/**
 * §7.18 Country Profile — per-country coverage drilldown.
 *
 * The breadth push (Phases A/B) means signals are spread unevenly across ~170
 * countries. This surface makes the per-country coverage legible: pick any
 * canonical country, see exactly which sources publish data for it, what
 * pathogens + metrics are covered, with per-row freshness. Countries with no
 * coverage today are visible too — that's the honest gap view, not hidden.
 */

import { useEffect, useMemo, useState } from 'react';
import { findPathogen } from '@biomax/plugin-pathogen-database';
import {
  listSignals, signalFreshness, CANONICAL_COUNTRIES, canonicalCountry,
} from '@biomax/plugin-surveillance-feed';
import { queryCulturalRiskFactors } from '@biomax/plugin-region-database';
import { Surface, Panel, KpiStrip, Kpi, DataTable, type DataColumn } from '../kit';
import { ProvChip, provKind } from '../ProvChip';
import { CulturalRiskSidebar } from '../cultural-risk/primitives';

function fmtAge(ms: number): string {
  const d = Math.floor(ms / 86_400_000);
  if (d >= 1) return `${d}d`;
  const h = Math.floor(ms / 3_600_000);
  return h >= 1 ? `${h}h` : `${Math.max(1, Math.floor(ms / 60_000))}m`;
}

/** Inline sparkline + WoW arrow for a CountryProfile pathogen row (CD R3b
 *  extension). Mirrors the Activity Indicators trend cell — reads
 *  `signal.history` (adapters that pull multi-week data populate it); em-dash
 *  placeholder for sources that only carry a latest observation. */
function CountrySignalTrend({ signal, stale }: { signal: ReturnType<typeof listSignals>[number]; stale: boolean }) {
  const hist = signal.history;
  if (!hist || hist.length < 2) {
    // CD R3b note: explicit "no history" affordance (em-dash alone reads as
    // flat/zero; a series-absence is a different fact than a stable trend).
    return (
      <span
        title="Source carries only a single observation; no trend series yet."
        style={{ display: 'inline-flex', alignItems: 'center', gap: 3, font: '600 10px var(--bm-font-mono)', color: 'var(--bm-text-subtle)', padding: '1px 5px', border: '1px dashed var(--bm-border-strong)', borderRadius: 4 }}
      >
        <span aria-hidden>∅</span>
        <span>no hist</span>
      </span>
    );
  }
  const values = hist.map(p => p.value);
  const w = 70, h = 22;
  const min = Math.min(...values), max = Math.max(...values), range = max - min || 1;
  const X = (i: number): number => (i / (values.length - 1)) * w;
  const Y = (v: number): number => h - ((v - min) / range) * (h - 4) - 2;
  const pts = values.map((v, i) => `${X(i)},${Y(v)}`).join(' ');
  const last = values[values.length - 1]!;
  const prev = values[values.length - 2]!;
  const up = last > prev, flat = last === prev;
  const col = stale ? 'var(--bm-prov-stale)' : up ? 'var(--bm-threat-2)' : 'var(--bm-prov-confirmed)';
  return (
    <span
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
      title={`${values.length}-point trend: ${values.map(v => v.toLocaleString()).join(' → ')}`}
    >
      <svg width={w} height={h} aria-hidden style={{ display: 'block' }}>
        <polyline points={pts} fill="none" stroke={col} strokeWidth="1.5" strokeLinejoin="round" />
        <circle cx={X(values.length - 1)} cy={Y(last)} r="2.2" fill={col} />
      </svg>
      <span style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 11, color: col }}>{flat ? '–' : up ? '▲' : '▼'}</span>
    </span>
  );
}

/** A signal "belongs" to a country either by ISO3 match or admin-name canonical lookup. */
function signalCountryIso3(s: ReturnType<typeof listSignals>[number]): string | null {
  if (s.location.iso3) return s.location.iso3;
  const cc = canonicalCountry(s.location.admin);
  return cc ? cc.iso3 : null;
}

export function CountryProfile() {
  // Index signals by ISO3 once.
  const byIso = useMemo(() => {
    const m = new Map<string, ReturnType<typeof listSignals>[number][]>();
    for (const s of listSignals()) {
      const iso = signalCountryIso3(s);
      if (!iso) continue;
      (m.get(iso) ?? m.set(iso, []).get(iso)!).push(s);
    }
    return m;
  }, []);

  // Default selection: the country with the most signals (USA today).
  const defaultIso = useMemo(() => {
    let best = 'USA', bestN = -1;
    for (const [iso, sigs] of byIso) if (sigs.length > bestN) { best = iso; bestN = sigs.length; }
    return best;
  }, [byIso]);

  // Initial country can come from the URL hash query (e.g. when Pathogen Watch's
  // map deep-links here via a country-rollup donut click).
  const initialIso = useMemo(() => {
    if (typeof window === 'undefined') return defaultIso;
    const q = window.location.hash.split('?')[1] ?? '';
    const params = new URLSearchParams(q);
    const iso = params.get('iso3');
    if (iso && /^[A-Z]{3}$/i.test(iso)) {
      const cc = canonicalCountry(iso);
      if (cc) return cc.iso3;
    }
    return defaultIso;
  }, [defaultIso]);

  const [iso, setIso] = useState<string>(initialIso);
  const [hideEmpty, setHideEmpty] = useState(true);

  // Re-sync if the hash query changes while the surface is mounted
  // (e.g. another map click during the same session).
  useEffect(() => {
    const onHash = () => {
      const q = window.location.hash.split('?')[1] ?? '';
      const params = new URLSearchParams(q);
      const next = params.get('iso3');
      if (next && /^[A-Z]{3}$/i.test(next)) {
        const cc = canonicalCountry(next);
        if (cc) setIso(cc.iso3);
      }
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const country = canonicalCountry(iso);
  // Phase 9.3 — cultural-risk factors for this country (iso3-scoped, all classes).
  const culturalFactors = useMemo(() => queryCulturalRiskFactors({ iso3: iso }), [iso]);
  const signals = byIso.get(iso) ?? [];
  const now = Date.now();

  // Source rollup for this country.
  const bySource = useMemo(() => {
    const m = new Map<string, { source: string; n: number; latest: string | null; latestStale: boolean }>();
    for (const s of signals) {
      const src = s.sources[0] ?? '?';
      const cur = m.get(src) ?? { source: src, n: 0, latest: null, latestStale: false };
      cur.n++;
      if (!cur.latest || s.asOf > cur.latest) {
        cur.latest = s.asOf;
        cur.latestStale = signalFreshness(s, now).stale;
      }
      m.set(src, cur);
    }
    return [...m.values()].sort((a, b) => b.n - a.n);
  }, [signals, now]);

  // Pathogen rollup for this country.
  const byPathogen = useMemo(() => {
    const m = new Map<string, { pathogen: string; metric: string; value: number; asOf: string; source: string; stale: boolean; raw: typeof signals[number] }>();
    for (const s of signals) {
      const key = `${s.pathogenId}|${s.metric}`;
      const cur = m.get(key);
      if (!cur || s.asOf > cur.asOf) {
        m.set(key, {
          pathogen: findPathogen(s.pathogenId)?.names.common ?? s.pathogenId,
          metric: s.metric,
          value: Number(s.value) || 0,
          asOf: s.asOf,
          source: s.sources[0] ?? '?',
          stale: signalFreshness(s, now).stale,
          raw: s,
        });
      }
    }
    return [...m.values()].sort((a, b) =>
      Number(a.stale) - Number(b.stale) || a.pathogen.localeCompare(b.pathogen));
  }, [signals, now]);

  // Country dropdown options: sorted by canonical name; optionally hide ones with 0 signals.
  const options = useMemo(() =>
    [...CANONICAL_COUNTRIES]
      .filter(c => !hideEmpty || byIso.has(c.iso3))
      .sort((a, b) => a.name.localeCompare(b.name)),
    [byIso, hideEmpty]);

  const sourceColumns: DataColumn<typeof bySource[number]>[] = [
    { key: 'src', header: 'Source', cell: r => r.source, width: '45%' },
    { key: 'n', header: 'Signals', num: true, cell: r => r.n, width: '15%' },
    { key: 'as', header: 'Latest', cell: r => <span style={{ color: 'var(--bm-text-muted)' }}>{r.latest?.slice(0, 10) ?? '—'}</span>, width: '20%' },
    { key: 'st', header: 'State', cell: r => r.latest ? <ProvChip kind={r.latestStale ? 'stale' : 'confirmed'} compact /> : <span style={{ color: 'var(--bm-text-muted)' }}>—</span>, width: '20%' },
  ];

  const pathogenColumns: DataColumn<typeof byPathogen[number]>[] = [
    { key: 'p', header: 'Pathogen', cell: r => r.pathogen, width: '22%' },
    { key: 'm', header: 'Metric', cell: r => <span style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 12 }}>{r.metric}</span>, width: '17%' },
    { key: 'v', header: 'Latest', num: true, cell: r => <span style={{ color: r.stale ? 'var(--bm-prov-stale)' : 'var(--bm-text)' }}>{r.value.toLocaleString()}</span>, width: '12%' },
    { key: 't', header: 'Trend', cell: r => <CountrySignalTrend signal={r.raw} stale={r.stale} />, width: '12%' },
    { key: 'as', header: 'As of', cell: r => <span style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 11, color: 'var(--bm-text-muted)' }}>{r.asOf.slice(0, 10)}</span>, width: '13%' },
    { key: 's', header: 'Source', cell: r => <span style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 11, color: 'var(--bm-text-subtle)' }}>{r.source}</span>, width: '14%' },
    { key: 'pv', header: 'State', cell: r => <ProvChip kind={provKind(r.raw, r.stale)} compact />, width: '10%' },
  ];

  const oldest = signals.reduce<number>((acc, s) => {
    const t = Date.parse(s.asOf);
    return Number.isFinite(t) && (acc === 0 || t < acc) ? t : acc;
  }, 0);
  const freshest = signals.reduce<number>((acc, s) => Math.max(acc, Date.parse(s.asOf) || 0), 0);
  const freshAge = freshest ? fmtAge(now - freshest) : '—';
  const oldestAge = oldest ? fmtAge(now - oldest) : '—';

  return (
    <Surface
      section="§7.18"
      title="Country Profile · per-country coverage drilldown"
      sub={<>Which sources publish data for this country, what pathogens + metrics they cover, and how fresh. The honest gap view — uncovered countries are visible (set "hide empty" off to see the long tail).</>}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '4px 0', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <span style={{ color: 'var(--bm-text-muted)' }}>Country:</span>
          <select
            value={iso}
            onChange={e => setIso(e.target.value)}
            style={{ font: '13px var(--bm-font-sans)', padding: '5px 10px', background: 'var(--bm-bg-subtle)', color: 'var(--bm-text)', border: '1px solid var(--bm-border)', borderRadius: 5, minWidth: 260 }}
          >
            {options.map(c => (
              <option key={c.iso3} value={c.iso3}>
                {c.name} ({c.iso3}){byIso.has(c.iso3) ? ` · ${byIso.get(c.iso3)!.length}` : ' · 0'}
              </option>
            ))}
          </select>
        </label>
        <label title="Hide canonical countries with no signals" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--bm-text-muted)', cursor: 'pointer' }}>
          <input type="checkbox" checked={hideEmpty} onChange={e => setHideEmpty(e.target.checked)} />
          hide countries with no coverage ({byIso.size}/{CANONICAL_COUNTRIES.length} have any)
        </label>
      </div>

      <KpiStrip>
        <Kpi label="Sources covering" value={bySource.length} sub={country?.name ?? iso} tone={bySource.length ? 'accent' : undefined} />
        <Kpi label="Pathogens × metrics" value={byPathogen.length} sub="distinct (pathogen, metric) pairs" />
        <Kpi label="Total signals" value={signals.length} sub="all sources, all dates" />
        <Kpi label="Freshest" value={freshAge} sub={freshest ? `latest from ${new Date(freshest).toISOString().slice(0, 10)}` : 'no data'} />
        <Kpi label="Oldest" value={oldestAge} sub={oldest ? `lagged refs visible` : '—'} />
      </KpiStrip>

      {signals.length === 0 ? (
        <Panel title="No coverage" meta={country?.name ?? iso}>
          <div style={{ padding: 'var(--bm-space-4)', fontSize: 13, color: 'var(--bm-text-muted)', lineHeight: 1.7 }}>
            No surveillance signals in the live feed for <strong>{country?.name ?? iso}</strong>. This is the honest "gap" answer
            — coverage requires either (1) a national open-data adapter for this country (see <code>docs/sources-deferred.md</code>),
            (2) WHO partner channel data, or (3) the country opting into a wired regional source (PAHO / Africa CDC / etc.). Not
            something we fabricate.
          </div>
        </Panel>
      ) : (
        <>
          <Panel title="Sources active for this country" meta={`${bySource.length} source${bySource.length === 1 ? '' : 's'}`}>
            <DataTable rows={bySource} rowKey={r => r.source} columns={sourceColumns} />
          </Panel>
          <Panel title="Pathogens × metrics — latest per pair" meta={`${byPathogen.length} pair${byPathogen.length === 1 ? '' : 's'} · stale demoted`}>
            <DataTable rows={byPathogen} rowKey={r => `${r.pathogen}|${r.metric}|${r.source}`} columns={pathogenColumns} scrollMaxHeight={420} />
          </Panel>
        </>
      )}

      {/* Phase 9.3 — Cultural / political context sidebar (CD R10).
          Country-scoped: every cultural-risk factor recorded for this iso3. */}
      <div style={{ marginTop: 'var(--bm-space-4)' }}>
        <CulturalRiskSidebar
          factors={culturalFactors}
          iso3={iso}
          title={`Cultural / political context · ${country?.name ?? iso}`}
        />
      </div>
    </Surface>
  );
}
